import React, { useState, useEffect } from 'react'
import type { EditableLogo } from 'paintscout'
import { makeStyles, ClickAwayListener } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { usePresentation } from '../../../context'
import { clearDrillInOnClickAway } from '../../util/presentationEditorDom'
import classnames from 'classnames'

interface InlineEditableLogoProps {
  classes?: StyleClasses<typeof useStyles>
  logo: EditableLogo
  logoOptionsProps: {
    blockPrefix: string
    fieldSuffix: string
  }
  isEditable: boolean
  handleEdit: (logo: EditableLogo) => void
  isSettings: boolean
}

const useStyles = makeStyles<Theme, InlineEditableLogoProps>(
  (theme: Theme) => {
    return {
      root: {
        position: 'relative'
      },
      hovered: {
        cursor: 'pointer',
        '&:hover': {
          outline: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
          zIndex: 1,
          '& $iconMenuRoot': {
            display: 'flex'
          }
        }
      },
      focused: {
        outline: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
        zIndex: 1
      },
      logoWrapper: (props) => ({
        width: '100%',
        display: 'flex',
        justifyContent: props.logo?.horizontalAlign || 'center'
      }),
      logo: (props) => ({
        height: 'auto',
        width: 'auto',
        maxHeight: 140,
        maxWidth: 140,
        ...(props.logo?.size === 'xs' && {
          maxHeight: 80,
          maxWidth: 80
        }),
        ...(props.logo?.size === 'sm' && {
          maxHeight: 110,
          maxWidth: 110
        }),
        ...(props.logo?.size === 'md' && {
          maxHeight: 140,
          maxWidth: 140
        }),
        ...(props.logo?.size === 'lg' && {
          maxHeight: 170,
          maxWidth: 170
        }),
        ...(props.logo?.size === 'xl' && {
          maxHeight: 200,
          maxWidth: 200
        })
      })
    }
  },
  { name: 'InlineEditableLogo' }
)

export default function InlineEditableLogo(props: InlineEditableLogoProps) {
  const classes = useStyles(props)
  const { logoOptionsProps, isEditable, handleEdit, isSettings } = props
  const [logo, setLogo] = useState(props.logo)

  // update logo in case of prop.logo change (e.g., logo to logo swap from ThreeColumnOptions)
  useEffect(() => {
    setLogo(props.logo)
  }, [props.logo])

  const { blockPrefix, fieldSuffix } = logoOptionsProps || {}
  // isFocused mirrors the parent block's own focus (isEditable) — see InlineEditableMedia for the
  // same pattern applied to images.
  const isFocused = isEditable
  const { focusedElement, setFocusedElement } = usePresentation()

  // this logo's slot in the inspector's drill-in state (#22201) — the formik path is unique per
  // rendered field, since blockPrefix carries the page/section/block indices
  const optionsKey = `logo::${blockPrefix}.${fieldSuffix}`
  const isDrilledIn = focusedElement?.type === 'logo' && focusedElement.key === optionsKey

  useEffect(() => {
    if (!isEditable) {
      // remove focus if parent block loses focus
      // instead of listening for a blur on the editor, we listen for a click outside of the block
      handleConfirm()
      if (isDrilledIn) {
        setFocusedElement(null)
      }
    }
    // This should only ever re-run on an isEditable transition (block focus changing), not on every
    // isDrilledIn/setFocusedElement/handleConfirm identity change — those are read for their current
    // value at the moment the block loses focus, not watched for their own changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable])

  const handleConfirm = (ev?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (ev) ev.stopPropagation()
    handleEdit(logo)
  }

  // not sure why react isn't rerendering when the logo styles change without this key setting hack but here we are
  const { src, ...logoStyles } = logo

  if (!isSettings)
    return (
      <div className={classes.logoWrapper}>
        <img className={classes.logo} src={src} />
      </div>
    )
  else
    return (
      <ClickAwayListener
        onClickAway={(ev) => {
          clearDrillInOnClickAway({ target: ev.target, isDrilledIn, setFocusedElement })
        }}
      >
        <div
          className={classnames(classes.root, {
            [classes.hovered]: !isFocused,
            [classes.focused]: isFocused
          })}
          onClick={(ev) => {
            // Block not focused yet: let the click bubble so it focuses the parent block first, same
            // as clicking any other part of the block.
            if (!isEditable) {
              return
            }

            // Block already focused: a single click on this logo drills straight into its own options.
            ev.stopPropagation()

            if (!isDrilledIn) {
              setFocusedElement({ type: 'logo', key: optionsKey })
            }
          }}
        >
          <div key={JSON.stringify(logoStyles)} className={classes.logoWrapper}>
            <img className={classes.logo} src={src} />
          </div>
        </div>
      </ClickAwayListener>
    )
}
